<template>

  <div v-if="error.length" class="error">
    Please correct the following errors:
    <ul>
      <li v-for="(err, index) in error" :key="index">{{ err }}</li>
    </ul>
  </div>

  <div class="cell small-12">
    <h1>Your company information</h1>
    <p>We have retrieved your company information from Companies House. </p>
    <p>Please ensure that the details are correct, and reflect the correct legal entity for your contract.</p>
  </div>
  
    <div class="cell small-12">
        <form class="fieldset" @submit.prevent>
          <div class="grid-x grid-margin-x">
            <div class="cell small-8">
              <label class="strong" for="name" >Company Name</label>
              <input type="text" id="name" name="name" placeholder="" v-model="formData.name" required>
              <label class="strong" for="addressline1">Company Address</label>
              <input type="text" id="addressline1" name="addressline1" placeholder="Address Line 1" v-model="formData.address.addressLine1" required>
              <input type="text" id="addressline2" name="addressline2" placeholder="Address Line 2" v-model="formData.address.addressLine2" required>
              <input type="text" id="addressline4" name="addressline3" placeholder="Address Line 3" v-model="formData.address.addressLine3" required>
              <input type="text" id="addressline4" name="addressline4" placeholder="Address Line 4" v-model="formData.address.addressLine4" required>
          </div>
          <div class="cell small-12"></div>
            <div class="cell small-4">
              <input type="text" id="postcode" name="postcode" placeholder="Postcode" class="w50pc" v-model="formData.address.postCode" required>
            </div>
            <div class="cell small-12"></div>
            <div class="cell small-8">
              <label class="strong" for="phonenumber">Phone Number</label>
              <input type="text" id="phonenumber" name="phonenumber" placeholder="" v-model="formData.phoneNumber" required>
            </div>
          </div>

        </form>
      </div>


</template>


<script setup>

import { ref, defineEmits } from 'vue'
import { postClientSignup } from '@/services/clientsignupmanager.func';
import { useRoute } from 'vue-router';

const route = useRoute();
const signupId = route.params.signupId;

const props = defineProps({
  formData: Object,
  clientInvite: Object
})


const error = ref([])

const validateStep = () => {
  error.value = []

  if (!props.formData.name || props.formData.name.trim().length < 2) {
    error.value.push('Please enter a valid company name.')
  }

  const phone = props.formData.phoneNumber || ''

  // Remove formatting characters: spaces, brackets and hyphens
  const normalisedPhone = phone.replace(/[\s()-]/g, '')

  // Only allow optional leading + followed by digits
  const phoneRegex = /^\+?\d+$/

  if (
    !normalisedPhone ||
    !phoneRegex.test(normalisedPhone) ||
    normalisedPhone.replace(/\D/g, '').length < 7 ||
    normalisedPhone.length > 15
  ) {
    error.value.push('Please enter a valid phone number.')
  }

  const address = props.formData.address || {}

  // Address Line 1: Required, MaxLength(100)
  if (!address.addressLine1 || address.addressLine1.trim() === '') {
    error.value.push('Address Line 1 is required.')
  } else if (address.addressLine1.length > 100) {
    error.value.push('Address Line 1 cannot exceed 100 characters.')
  }

  // Address Line 2: Optional, MaxLength(100)
  if (address.addressLine2 && address.addressLine2.length > 100) {
    error.value.push('Address Line 2 cannot exceed 100 characters.')
  }

  // Address Line 3: Optional, MaxLength(100)
  if (address.addressLine3 && address.addressLine3.length > 100) {
    error.value.push('Address Line 3 cannot exceed 100 characters.')
  }

  // Address Line 4: Optional, MaxLength(100)
  if (address.addressLine4 && address.addressLine4.length > 100) {
    error.value.push('Address Line 4 cannot exceed 100 characters.')
  }

  // Keep address lines sequential
  if (
    address.addressLine2 &&
    (!address.addressLine1 || address.addressLine1.trim() === '')
  ) {
    error.value.push('Address Line 2 cannot be filled if Address Line 1 is empty.')
  }

  if (
    address.addressLine3 &&
    (!address.addressLine2 || address.addressLine2.trim() === '')
  ) {
    error.value.push('Address Line 3 cannot be filled if Address Line 2 is empty.')
  }

  if (
    address.addressLine4 &&
    (!address.addressLine3 || address.addressLine3.trim() === '')
  ) {
    error.value.push('Address Line 4 cannot be filled if Address Line 3 is empty.')
  }
  // PostCode: Required, MaxLength(15)
  const postcode = address.postCode || ''

  if (!postcode.trim()) {
    error.value.push('Postcode is required.')
  } else if (postcode.length > 15) {
    error.value.push('Postcode cannot exceed 15 characters.')
  } else {
    const postcodeRegex =
      /^[A-Z]{1,2}[0-9R][0-9A-Z]? ?[0-9][ABD-HJLNP-UW-Z]{2}$/i

    if (!postcodeRegex.test(postcode.trim())) {
      error.value.push('Please enter a valid UK postcode.')
    }
  }

  return error.value.length === 0
}


const emit = defineEmits(['update-form']);
import { getPersonsWithSignificantControl } from '@/services/companieshousemanager.func';
const errorMessage = 'Sorry, we\'ve been unable to locate your company\'s persons with significant control, please contact your Qtac account manager.';
const executeStep = async () => {

    if(props.formData.contacts.length != 0) {
      return true;
    }


    if(props.clientInvite.clientType == 'IntermediaryClient') {
      await submitToAPIForIntermediarySignup();
      return true;
    }


    try {
    const res = await getPersonsWithSignificantControl(props.formData.companyNumber);
    if(res.status !== 200) {
      error.value = errorMessage;
      return false;
    }
    var mappedResponse = personsWithSignificantControlMapper(res.data);
    emit('update-form', mappedResponse);
  } catch (err) {
    error.value = errorMessage;
  }

  return true;
}

const submitToAPIForIntermediarySignup = async () => {
  var request = {
    client: props.formData,
    signupDeadlineDate: props.clientInvite.signupDeadline
  }

  const res = await postClientSignup(request, signupId);
  if (res.status !== 202) {
    error.value.push('Sorry, we\'ve been unable to create your account, please contact your Qtac account manager.');
    return false;
  }
}


const personsWithSignificantControlMapper = (res) => {

  const activeSignificantControllers = res.items.filter(item => item.ceased === false);


  const contacts = activeSignificantControllers.map(controller => {
  const forename = controller.name_elements?.forename || '';
  const surname = controller.name_elements?.surname || '';

    return {
      firstName: forename,
      lastName: surname, 
      jobTitle: 'Director',
      isVerificationRequired: true,
      isSlaSignatory: false,
      isDirectDebitAuthoriser: false
    };
  });

  const updatedFormData = {
    ...props.formData,
    contacts
  };

  return updatedFormData;

};


defineExpose({ validateStep, executeStep });

</script>